import React, { useEffect, useRef } from 'react'
import { IVideoData } from '../@types/data'
import { useData } from '../context/DataContext'
import { Position } from '../helpers/constant'
import NavButton from '../ui/NavButton'

interface IProps {
  data: IVideoData
}

const ContentPanel: React.FC<IProps> = ({ data }: IProps) => {
  const bodyWrapperRef = useRef<HTMLDivElement>(null)
  const { position, label, subheader } = data
  const {
    setRequestedVideo,
    requestedVideo,
    selectedVideo,
    requestedChapter,
    setRequestedChapter,
    activeChapter,
    setActiveChapter,
    nextChapter,
    timerProgress,
    copy,
    videos,
  } = useData()
  let activeTimeout: number | null
  const otherVideo =
    videos !== undefined
      ? position === Position.LEFT
        ? videos[1]
        : videos[0]
      : null

  useEffect(() => {
    if (requestedChapter === activeChapter) return
    if (activeTimeout != null) clearTimeout(activeTimeout)
    activeTimeout = window.setTimeout(() => {
      if (requestedChapter !== undefined) {
        if (bodyWrapperRef?.current != null) {
          bodyWrapperRef.current.scrollTop = 0
        }
        setActiveChapter(requestedChapter)
      }
    }, 300)
  }, [requestedChapter])

  const goToNextChapter = (): void => {
    if (nextChapter != null) {
      setRequestedChapter(nextChapter)
    }
  }

  // const handleTransitionEnd = (): void => {
  //   if (requestedChapter !== undefined && requestedChapter !== activeChapter) {
  //     setActiveChapter(requestedChapter)
  //   }
  // }

  return (
    <div
      className={`relative w-full h-full bg-gray-darkest border-t-[1px] border-gray-border lg:border-t-0 ${
        position === Position.RIGHT
          ? 'lg:border-l-[1px] lg:border-r-0'
          : 'lg:border-r-[1px] lg:border-l-0'
      }`}
    >
      <div
        className={`flex flex-col items-center justify-between h-full p-5 md:p-6 lg:p-5 xl:p-7 2xl:p-9 ${
          position === Position.RIGHT ? 'flex-row-reverse' : 'flex-row'
        }`}
      >
        <div className="w-[100%] flex flex-col h-full overflow-hidden self-start lg:w-auto relative after:absolute after:w-full after:h-8 md:after:h-12 after:bottom-0 after:from-gray-darkest after:to-gray-darkest-0 ">
          <h1 className="sr-only">{label}</h1>
          <h2 className="uppercase font-bold text-white text-base lg:text-lg 2xl:text-xl tracking-wide">
            {subheader}
          </h2>
          {activeChapter !== undefined && (
            <>
              <h3
                className={`max-w-[80%] text-2xl leading-7 lg:max-w-none md:text-[1.625rem] md:leading-[1.875rem] 2xl:text-3xl 2xl:leading-8 uppercase text-gold tracking-wide mb-2 mt-4 sm:mt-4 lg:mt-8 xl:mt-16 transition-opacity duration-300 ${
                  (requestedChapter !== null &&
                    requestedChapter !== activeChapter) ||
                  requestedVideo !== selectedVideo
                    ? 'opacity-0'
                    : 'opacity-100'
                } ${
                  requestedVideo === selectedVideo &&
                  requestedChapter === activeChapter
                    ? 'delay-300'
                    : ''
                }`}
              >
                {activeChapter.label}
              </h3>
              <div
                className={`relative flex-1 overflow-hidden transition-opacity duration-300 ${
                  (requestedChapter !== null &&
                    requestedChapter !== activeChapter) ||
                  requestedVideo !== selectedVideo
                    ? 'opacity-0'
                    : 'opacity-100'
                } ${
                  requestedVideo === selectedVideo &&
                  requestedChapter === activeChapter
                    ? 'delay-300'
                    : ''
                }  after:absolute after:w-full after:h-8 md:after:h-12 after:bottom-0 after:bg-gradient-to-t after:from-gray-darkest after:to-gray-darkest-0`}
              >
                <div
                  ref={bodyWrapperRef}
                  className="h-full overflow-scroll pb-8 md:pb-12"
                >
                  <p
                    className="text-white-0.75 text-sm md:text-base lg:text-lg whitespace-pre-line"
                    dangerouslySetInnerHTML={{ __html: activeChapter.body }}
                  />
                </div>
              </div>
              <div className="flex flex-col w-full h-[110px] justify-between z-10 mt-6 sm:h-auto sm:flex-row sm:items-end sm:mt-4 lg:min-h-[120px] xl:min-h-[130px] 2xl:min-h-[136px] lg:flex-col lg:items-start lg:justify-between lg:mt-12">
                <div
                  className={`w-full sm:mr-2 lg:mr-auto lg:w-full transition-opacity ${
                    timerProgress > 0 &&
                    nextChapter != null &&
                    requestedVideo === selectedVideo
                      ? 'opacity-100'
                      : 'opacity-0'
                  }`}
                >
                  <p className="font-header uppercase mb-1 text-xs tracking-widest">
                    {copy?.NEXT_CHAPTER_PREFIX}
                  </p>
                  <button
                    className="stat-button-link group relative flex w-full justify-center items-center z-10"
                    onClick={goToNextChapter}
                    data-dtm={selectedVideo?.key}
                  >
                    <div
                      className="absolute w-full h-full bg-gold-hover origin-left scale-x-0"
                      style={{ transform: `scaleX(${timerProgress}%)` }}
                    ></div>
                    <div className="w-full z-20 p-2 border-[1px] lg:border-2 border-gold duration-100 transition-colors bg-black-0.3 group-hover:bg-gold-hover group-active:bg-gray-light-press font-header uppercase font-bold text-white tracking-widest text-sm 2xl:text-base">
                      {nextChapter?.label}
                    </div>
                  </button>
                </div>
                {videos != null && otherVideo !== null && (
                  <div className="self-end w-full sm:ml-2 lg:ml-auto lg:w-full sm:self-auto">
                    <NavButton
                      label={otherVideo.label}
                      isSelected={false}
                      onClick={() => {
                        setRequestedVideo(otherVideo)
                      }}
                      reversed={position === Position.LEFT}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* <div className="w-[45%] md:w-full max-w-[240px] max-h-[185px] mt-6">
          <img className="w-full" src="./images/engine-drawing.png" />
        </div> */}
      </div>
    </div>
  )
}

export default ContentPanel
